import { Box, Icon, Typography, useTheme } from "@outschool/backpack";
import { EnrollmentMeetingPurchaseQueryQuery } from "@outschool/gql-frontend-generated";
import { faCalendarXmark, falTimes } from "@outschool/icons";
import { TFunction, useTranslation } from "@outschool/localization";
import { dayjs, pluralizeDayOfWeek, startEndTime } from "@outschool/time";
import { useSession } from "@outschool/ui-auth";
import {
  IconButton,
  Loading,
  ModalActionButtons,
  Space
} from "@outschool/ui-legacy-component-library";
import React from "react";

import { EnrollmentDurationSelect } from "./enrollmentForm/EnrollmentDurationSelect";
import { useFutureMeetings } from "./EnrollmentMeetingPurchaseDuration";
import { FutureMeetingInfo } from "./types";

export type EnrollmentMeetingPurchaseDurationProps = {
  duration?: number | null;
  futureMeetings: FutureMeetingInfo[];
  isContinuousSchedulingAvailable: boolean;
  onBack?(e: React.SyntheticEvent): void;
  onClose(e: React.SyntheticEvent, resetFields?: boolean): void;
  onNextClick(duration: number): void;
  canChangeDuration?: boolean;
  showCloseButton?: boolean;
  isPrepayEnrollment?: boolean;
  durationsWithAvailability?: number[];
};

export default function EnrollmentMeetingPurchaseFutureMeetings({
  duration,
  futureMeetings,
  isContinuousSchedulingAvailable,
  canChangeDuration = true,
  showCloseButton = true,
  isPrepayEnrollment = false,
  onBack,
  onClose,
  onNextClick,
  durationsWithAvailability
}: EnrollmentMeetingPurchaseDurationProps) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\EnrollmentMeetingPurchaseFutureMeetings"
  );
  const [enrollmentDuration, setEnrollmentDuration] = React.useState(duration);
  const onNext = (e: MouseEvent) => {
    e.preventDefault();
    // @ts-ignore TS(2345): Argument of type 'number | undefined' is not assig... Remove this comment to see the full error message
    onNextClick(enrollmentDuration);
  };
  return (
    <form method="POST" onSubmit={e => e.preventDefault()}>
      <Box
        flex
        sx={{
          gap: "1em",
          flexDirection: "column",
          alignItems: "stretch",
          justifyContent: "space-between",
          padding: "24px 0"
        }}
      >
        {canChangeDuration && (
          <EnrollmentDurationSelect
            // @ts-ignore TS(2322): Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
            enrollmentDuration={enrollmentDuration}
            setEnrollmentDuration={setEnrollmentDuration}
            isRecurringSchedulingAvailable={isContinuousSchedulingAvailable}
            isPrepayEnrollment={isPrepayEnrollment}
            durationsWithAvailability={durationsWithAvailability}
          />
        )}
        <DatesDisplay
          futureMeetings={futureMeetings}
          // @ts-ignore TS(2322): Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
          duration={enrollmentDuration}
          t={t}
        />
        <UnavailableNotice
          futureMeetings={futureMeetings}
          // @ts-ignore TS(2322): Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
          duration={enrollmentDuration}
          t={t}
        />
        {showCloseButton && (
          <Box
            sx={{
              position: "absolute",
              top: "8px",
              right: "8px",
              zIndex: 1
            }}
          >
            <IconButton
              icon={falTimes}
              sx={{
                fontSize: "large",
                color: "text",
                "&:hover": {
                  color: "#380596"
                }
              }}
              iconProps={{ sx: { fontSize: "1.333em" } }}
              onClick={onClose}
              aria-label={t("Close")}
            />
          </Box>
        )}
      </Box>
      <ModalActionButtons
        cancelLabel={t("Go Back")}
        hideCancelButton={!onBack}
        onCancel={onBack}
        primaryLabel={t("Next")}
        immediatePrimaryAction={onNext}
        trackingName="continue_to_learner_selection"
        trackingSharedProperties={{
          enrollmentDuration: duration
        }}
      />
    </form>
  );
}

const UnavailableNotice = ({
  futureMeetings,
  duration,
  t
}: {
  futureMeetings: FutureMeetingInfo[];
  duration: number;
  t: TFunction;
}) => {
  const checkAvailableWeeks = duration || 12;
  const theme = useTheme();
  if (
    !futureMeetings.some(
      (meeting, i) => !meeting.isAvailable && i < checkAvailableWeeks
    )
  ) {
    return <></>;
  }

  return (
    <Box
      flex
      sx={{
        gap: ".5em",
        flexDirection: "row",
        justifyContent: "space-between",
        color: "grey.400"
      }}
    >
      <Icon
        icon={faCalendarXmark}
        sx={{
          width: "1.25em",
          paddingTop: ".25em",
          color: theme.palette.error[600]
        }}
      />
      <Box
        flex
        sx={{
          flexDirection: "column",
          justifyContent: "space-between"
        }}
      >
        <Typography sx={{ fontWeight: "fontWeightBold", fontSize: "medium" }}>
          {t`The teacher is unavailable on these dates and times`}
        </Typography>
        <Typography sx={{ fontSize: "medium" }}>
          {t`Add or reschedule meetings later, and keep the weekly learning routine.`}
        </Typography>
      </Box>
    </Box>
  );
};

const DatesDisplay = ({
  futureMeetings,
  duration,
  t
}: {
  futureMeetings: FutureMeetingInfo[];
  duration: number;
  t: TFunction;
}) => {
  const { userTimeZone } = useSession();
  const { startTime: firstMeetingStart, endTime: firstMeetingEnd } =
    futureMeetings?.[0];
  const displayableMeetings = !!duration
    ? futureMeetings.slice(0, duration)
    : futureMeetings;
  const theme = useTheme();

  return (
    <Box
      sx={{
        backgroundColor: "grey.50",
        borderRadius: "8px",
        borderWidth: "2px",
        padding: "1em",
        // @ts-ignore TS(1117): An object literal cannot have multiple properties ... Remove this comment to see the full error message
        backgroundColor: "primary.50"
      }}
    >
      <Typography sx={{ fontWeight: "fontWeightBold" }}>
        {t("{{dayOfWeek}} {{timeOfDay}}", {
          // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
          dayOfWeek: pluralizeDayOfWeek(firstMeetingStart, userTimeZone),
          timeOfDay: startEndTime(
            firstMeetingStart,
            firstMeetingEnd,
            // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
            userTimeZone
          )
        })}
      </Typography>
      <Space y="small" />
      <Typography sx={{ fontWeight: "fontWeightMedium", fontSize: "medium" }}>
        {t`Dates you are booking`}
      </Typography>
      <Box
        sx={{
          paddingTop: ".5em",
          display: "grid",
          gridGap: ".5em",
          gridTemplateColumns: "1fr 1fr 1fr 1fr"
        }}
      >
        {displayableMeetings.map(meeting => {
          return (
            <Box
              sx={{
                textAlign: "center",
                borderRadius: ".25em",
                backgroundColor: "white",
                color: !meeting.isAvailable ? "grey.600" : "",
                textDecoration: !meeting.isAvailable ? "line-through" : "",
                padding: ".25em"
              }}
              key={`meeting_${meeting.startTime}`}
            >
              {!meeting.isAvailable && (
                <Icon
                  icon={faCalendarXmark}
                  sx={{
                    width: "1.25em",
                    marginRight: "0.5em",
                    color: theme.palette.error[600]
                  }}
                />
              )}
              <Typography sx={{ fontSize: "medium", fontWeight: 500 }}>
                {dayjs(meeting.startTime).format("MMM DD")}
              </Typography>
            </Box>
          );
        })}
        {!duration && (
          <Box
            sx={{
              padding: ".25em",
              textAlign: "center",
              borderRadius: ".25em",
              backgroundColor: "white",
              gridColumn: "1/5"
            }}
          >
            <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
              {t`Future meetings will be automatically added as you go.`}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export function EnrollmentMeetingPurchaseFutureMeetingsWrapper({
  activity,
  startTime,
  endTime,
  orgTeacher,
  handleMakeItOngoingSubmit,
  handleCheckoutFormCancelAndClose,
  isPrepayEnrollment
}: {
  // @ts-ignore TS(2339): Property 'activity' does not exist on type '{ __ty... Remove this comment to see the full error message
  activity: EnrollmentMeetingPurchaseQueryQuery["enrollmentMeetingPurchaseForm"]["activity"];
  startTime: Date;
  endTime: Date;
  // @ts-ignore TS(2339): Property 'orgTeacher' does not exist on type '{ __ty... Remove this comment to see the full error message
  orgTeacher?: EnrollmentMeetingPurchaseQueryQuery["enrollmentMeetingPurchaseForm"]["orgTeacher"];
  handleMakeItOngoingSubmit: ({
    duration,
    canScheduleContinuously,
    futureMeetings
  }: {
    duration: number;
    canScheduleContinuously: boolean;
    futureMeetings: FutureMeetingInfo[];
  }) => void;
  handleCheckoutFormCancelAndClose: (
    e?: React.SyntheticEvent,
    resetFields?: boolean
  ) => void;
  isPrepayEnrollment?: boolean;
}) {
  const {
    duration,
    canScheduleContinuously,
    futureMeetings,
    futureMeetingsLoading,
    durationsWithAvailability
  } = useFutureMeetings({
    activityUid: activity.uid,
    startTime,
    endTime,
    enrollmentDuration: null,
    orgTeacherUid: orgTeacher?.uid
  });

  if (futureMeetingsLoading) {
    return <Loading />;
  }

  return (
    <EnrollmentMeetingPurchaseFutureMeetings
      duration={duration}
      onClose={handleCheckoutFormCancelAndClose}
      futureMeetings={futureMeetings}
      isContinuousSchedulingAvailable={canScheduleContinuously}
      onNextClick={dur => {
        handleMakeItOngoingSubmit({
          duration: dur,
          canScheduleContinuously,
          futureMeetings
        });
      }}
      isPrepayEnrollment={isPrepayEnrollment}
      durationsWithAvailability={durationsWithAvailability}
    />
  );
}
